import {
  File,
  InitialFieldsWithFileName,
  Signatory,
  Signature,
} from "@/type/core/dashboard/dashboard.types";
import { ModelTransaction } from "@/type/core/models/models.types";
import {
  SignatureRequirementMode,
  TransactionDetail,
  TransactionType,
  TransactionUpdateField,
} from "@type/core/transaction/transaction.types";
import { createEvent } from "effector";

export type ResetPicture = Pick<
  Signature,
  | "email"
  | "rank"
  | "pattern"
  | "signaturePictureRank"
  | "signaturePriorityRule"
  | "identificator"
>;
export type PicturePriority = Pick<
  Signature,
  "email" | "signaturePictureRank" | "signaturePriorityRule"
>;
export type ChangePictureName = Pick<Signature, "name" | "id" | "order">;
export type MoveSignature = Pick<
  Signature,
  | "id"
  | "email"
  | "positionX"
  | "positionY"
  | "page"
  | "order"
  | "isMoved"
  | "pattern"
  | "rank"
  | "identificator"
>;
export type DeleteSignature = Pick<Signature, "id" | "identificator">;

export type SignatoryPattern = Pick<Signatory, "email" | "pattern">;
export type SignatoryLevel = Pick<Signatory, "email" | "level">;
export type SignatoryWithCertificate = Pick<
  Signatory,
  "email" | "withCertificate"
>;

export type RemoveTransactionFilePayload = { key: string };

export const setCurrentTransactionDetail = createEvent<TransactionDetail>(
  "SET_CURRENT_TRANSACTION_DETAIL",
);

export const setCurrentTransactionField = createEvent<TransactionUpdateField[]>(
  "SET_CURRENT_TRANSACTION_FIELD",
);

export const removeTransactionFile = createEvent<RemoveTransactionFilePayload>(
  "REMOVE_CURRENT_TRANSACTION_FILE",
);

export const setTransactionType = createEvent<TransactionType>(
  "SET_TRANSACTION_TYPE",
);

export const setTransactionFiles = createEvent<File[]>("SET_TRANSACTION_FILES");

export const setTransactionAttachments = createEvent<File[]>(
  "SET_TRANSACTION_ATTACHMENTS",
);

export const removeTransactionAttachment =
  createEvent<RemoveTransactionFilePayload>("REMOVE_TRANSACTION_ATTACHMENT");

export const setTransactionSignatories = createEvent<Signatory[]>(
  "SET_TRANSACTION_SIGNATORIES",
);

export const setTransactionSignatures = createEvent<Signature[]>(
  "SET_TRANSACTION_SIGNATURES",
);

export const addTransactionSignature = createEvent<Signature>(
  "ADD_TRANSACTION_SIGNATURE",
);
export const resetTransactionSignature = createEvent<ResetPicture>(
  "RESET_TRANSACTION_SIGNATURE",
);
export const changeTransactionSignatureName = createEvent<ChangePictureName>(
  "CHANGE_TRANSACTION_SIGNATURE_NAME",
);
export const moveTransactionSignature = createEvent<MoveSignature>(
  "MOVE_TRANSACTION_SIGNATURE",
);
export const moveModelOrScellementTransactionSignature =
  createEvent<MoveSignature>("MOVE_MODEL_TRANSACTION_SIGNATURE");
export const deleteTransactionSignature = createEvent<DeleteSignature>(
  "DELETE_TRANSACTION_SIGNATURE",
);

export const changeTransactionSignaturePicturePriorityForAll =
  createEvent<PicturePriority>(
    "CHANGE_TRANSACTION_SIGNATURE_PICTURE_PRIORITY_FOR_ALL",
  );

export const changeSignatureFromFirstRank = createEvent<void>(
  "CHANGE_SIGNATURE_FROM_FIRST_RANK",
);

export const updateFileWithPageNumber = createEvent<File>(
  "UPDATE_FILE_WITH_PAGE_NUMBER",
);

export const resetTransaction = createEvent<void>("RESET_TRANSACTION");

export const setCurrentTransactionFromModel = createEvent<ModelTransaction>(
  "SET_CURRENT_TRANSACTION_FROM_MODEL",
);

export const afterTransactionFieldUpdated = createEvent<
  TransactionUpdateField[]
>("AFTER_TRANSACTION_FIELD_UPDATED");

export const setTransactionIsFromDraft = createEvent<boolean>(
  "SET_TRANSACTION_IS_FROM_DRAFT",
);

export const setSignatoryLevel = createEvent<SignatoryLevel>(
  "SET_SIGNATORY_LEVEL",
);

export const setSignatoryPattern = createEvent<SignatoryPattern>(
  "SET_SIGNATORY_PATTERN",
);

export const setSignatoryWithCertificate =
  createEvent<SignatoryWithCertificate>("SET_SIGNATORY_WITH_CERTIFICATE");

export const setSignatoryPhoneIsUserJSE = createEvent<string>(
  "SET_SIGNATORY_PHONE_IS_USER_JSE",
);

export const applyInitialFieldsToFile = createEvent<InitialFieldsWithFileName>(
  "APPLY_INITIAL_FIELDS_TO_FILE",
);

export const removeInitialFieldsFromFileName = createEvent<string | undefined>(
  "REMOVE_INITIAL_FIELDS_FROM_FILE",
);

export const applyFirstInitialFieldsToOthers = createEvent<void>(
  "APPLY_FIRST_INITIAL_FIELDS_TO_OTHER",
);

export const setSignatureRequirementMode =
  createEvent<SignatureRequirementMode>("SET_SIGNATURE_REQUIREMENT_MODE");
